import { Ordering } from '@/types/graphql';
import { queuesForVariation } from '@axle-health/work-queue-definitions';

import {
    defaultEpisodeOfCareRowToLink,
    defaultEpisodeVisitRequestsRowToLink,
    defaultEpisodeVisitsRowToLink,
    defaultVisitRequestsRowToLink,
    defaultVisitsRowToLink,
} from '../definitions';
import { definitionFor, fromDefinition } from '../fromDefinition';
import {
    EpisodeOfCareDashboardContainer,
    PatientDashboardContainer,
    VisitDashboardContainer,
    VisitRequestDashboardContainer,
} from '../types';
import { HOSPICE_DEMO_CONTAINER_KEYS } from './containerKeys/containerKeys';
import { IDashboardVariationConfig } from './types';

const PUBLISHED_QUEUES = queuesForVariation('HOSPICE_DEMO');

const patientSchedulingLink = ({ id }: { id: string }): string => `/patients/${id}/scheduling`;

const EVAL_VISIT_REQUESTS_CONTAINER: VisitRequestDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HOSPICE_DEMO_CONTAINER_KEYS.evalVisitRequests),
    {
        defaultEnabled: true,
        hoverText: 'Eval visit requests that need scheduling.',
        tableConfig: {
            rowToLink: defaultEpisodeVisitRequestsRowToLink,
            ordering: { patient: { fullName: Ordering.ASC } },
        },
    },
);

const ADMISSIONS_VISITS_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HOSPICE_DEMO_CONTAINER_KEYS.admissionsVisits),
    {
        defaultEnabled: true,
        hoverText: 'Upcoming admission visits that need scheduling.',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultEpisodeVisitsRowToLink,
            emptyTableText: 'No admission visits to schedule.',
            ordering: { fulfillmentWindowEndDate: Ordering.ASC },
        },
    },
);

const HUV_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HOSPICE_DEMO_CONTAINER_KEYS.huv),
    {
        defaultEnabled: true,
        hoverText: 'Upcoming HUVs that need scheduling.',
        tableConfig: {
            rowToLink: patientSchedulingLink,
            emptyTableText: 'No HUVs to schedule.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const INCOMPLETE_CARE_TEAM_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HOSPICE_DEMO_CONTAINER_KEYS.incompleteCareTeam),
    {
        defaultEnabled: true,
        hoverText: 'Patients that need care teams assigned.',
        tableConfig: {
            rowToLink: patientSchedulingLink,
            emptyTableText: 'No patients with incomplete care teams.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const PATIENTS_NEEDING_SCHEDULING_CONTAINER: EpisodeOfCareDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HOSPICE_DEMO_CONTAINER_KEYS.patientsNeedingScheduling),
    {
        defaultEnabled: true,
        hoverText: 'Patients that need scheduling.',
        tableConfig: {
            rowToLink: defaultEpisodeOfCareRowToLink,
            emptyTableText: 'No patients to schedule.',
            ordering: { startDate: Ordering.ASC },
        },
    },
);

const UNASSIGNED_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HOSPICE_DEMO_CONTAINER_KEYS.unassigned),
    {
        defaultEnabled: true,
        hoverText: 'Visits in need of a clinician assignment.',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No unassigned visits at this time',
            ordering: { startDatetime: Ordering.ASC },
        },
    },
);

const SFV_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HOSPICE_DEMO_CONTAINER_KEYS.sfv),
    {
        defaultEnabled: true,
        hoverText: 'Follow-up visits required within 2 days of Symptom Impact Assessments.',
        extraProps: { showClinicianColumn: false },
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: ({ patient }: { patient: { id: string } }) =>
                `/patients/${patient.id}/scheduling`,
            emptyTableText: 'No SFVs need attention at this time.',
            ordering: { startDatetime: Ordering.ASC },
        },
    },
);

const EXPIRING_FULFILLMENT_WINDOW_CONTAINER: VisitRequestDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HOSPICE_DEMO_CONTAINER_KEYS.expiringFulfillmentWindow),
    {
        defaultEnabled: true,
        hoverText: 'Visits with expiring fulfillment windows.',
        tableConfig: {
            rowToLink: defaultVisitRequestsRowToLink,
            emptyTableText: 'No visits with expiring fulfillment windows.',
            ordering: { fulfillmentWindowEndDate: Ordering.ASC },
        },
    },
);

const PATIENT_NO_SHOWS_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HOSPICE_DEMO_CONTAINER_KEYS.patientNoShows),
    {
        defaultEnabled: true,
        hoverText: 'Patient no-shows requiring attention',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No patient no-shows requiring attention.',
            ordering: { startDatetime: Ordering.DESC },
        },
    },
);

const RECERT_CONTAINER: EpisodeOfCareDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HOSPICE_DEMO_CONTAINER_KEYS.recert),
    {
        defaultEnabled: true,
        hoverText: 'Upcoming Recerts that need scheduling.',
        tableConfig: {
            rowToLink: defaultEpisodeOfCareRowToLink,
            emptyTableText: 'No Recerts to schedule.',
            ordering: { patient: { fullName: Ordering.ASC } },
        },
    },
);

export const HOSPICE_DEMO_DASHBOARD_CONFIG: IDashboardVariationConfig = {
    containers: [
        EVAL_VISIT_REQUESTS_CONTAINER,
        ADMISSIONS_VISITS_CONTAINER,
        HUV_CONTAINER,
        INCOMPLETE_CARE_TEAM_CONTAINER,
        PATIENTS_NEEDING_SCHEDULING_CONTAINER,
        UNASSIGNED_CONTAINER,
        SFV_CONTAINER,
        EXPIRING_FULFILLMENT_WINDOW_CONTAINER,
        PATIENT_NO_SHOWS_CONTAINER,
        RECERT_CONTAINER,
    ],
};
